import React from 'react';
import { Link } from 'react-router-dom';
import { openCookieSettings } from './CookieConsent';
import { CITIES, FACEBOOK_URL, INSTAGRAM_URL, LINKEDIN_URL, X_URL } from '../lib/seo';
import facebookIcon from '../assets/icons8-facebook-48.png';
import instagramIcon from '../assets/icons8-instagram-48.png';
import linkedinIcon from '../assets/icons8-linkedin-48.png';

/**
 * The X wordmark, inline rather than an icon file: it is a single flat glyph,
 * and drawing it in `currentColor` keeps it crisp at any size next to the
 * bitmap icons it sits with.
 */
const XIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LINK = 'text-[#167062] hover:text-[#0f524a] transition-colors';

const COLUMN_HEADING = 'text-xs font-semibold uppercase tracking-widest text-gray-900';

/** The pages that are not a city map, in the order they matter. */
const SITE_LINKS = [
  { label: 'Get the app', to: '/app' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Our team', to: '/team' },
  { label: 'Support', to: '/support' },
  { label: 'Privacy Policy', to: '/privacy-policy' },
];

/**
 * The site's link hub.
 *
 * Every page renders this, which is the point: the city maps are the pages
 * worth ranking and they are otherwise reachable only through a dropdown in
 * the header that a crawler cannot open. Listing them here gives every city
 * one internal link from every page on the site, and gives a visitor who read
 * to the bottom of the story somewhere to go next. The blueprint this
 * implements is written out in SEO.md.
 */
export const Footer: React.FC = () => {
  return (
    <footer className="border-t border-gray-100 bg-white py-12">
      <div className="mx-auto max-w-5xl px-4">
        {/* ---------------- Link columns ---------------- */}
        <nav
          aria-label="Footer"
          className="grid grid-cols-2 gap-8 text-left sm:grid-cols-4"
        >
          <div className="col-span-2">
            <h2 className={COLUMN_HEADING}>Beer prices by city</h2>
            <ul className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              {CITIES.map((city) => (
                <li key={city.slug}>
                  <Link to={`/${city.slug}`} className={LINK}>
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className={COLUMN_HEADING}>Centmap</h2>
            <ul className="mt-4 space-y-2 text-sm">
              {SITE_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={LINK}>
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                {/* Consent has to be as easy to withdraw as it was to give. */}
                <button type="button" onClick={openCookieSettings} className={LINK}>
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h2 className={COLUMN_HEADING}>Social</h2>
            {/* The same profiles as `Organization.sameAs` in
                public/index.html — keep the two lists in step. */}
            <div className="mt-4 flex gap-4">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Centmap on Facebook"
                className="transition-opacity hover:opacity-70"
              >
                <img src={facebookIcon} alt="Facebook" className="h-7 w-7" />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Centmap on Instagram"
                className="transition-opacity hover:opacity-70"
              >
                <img src={instagramIcon} alt="Instagram" className="h-7 w-7" />
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Centmap on LinkedIn"
                className="transition-opacity hover:opacity-70"
              >
                <img src={linkedinIcon} alt="LinkedIn" className="h-7 w-7" />
              </a>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Centmap on X"
                className="text-gray-900 transition-opacity hover:opacity-70"
              >
                <span className="block h-7 w-7 p-0.5">
                  <XIcon />
                </span>
              </a>
            </div>
          </div>
        </nav>

        {/* ---------------- Copyright ---------------- */}
        <p className="mt-12 border-t border-gray-100 pt-8 text-center text-sm text-gray-400">
          Copyright &copy; 2025 Centmap LLC - All Rights Reserved.
        </p>
      </div>
    </footer>
  );
};
